import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import { ChevronDown, Info, Trash2, X } from 'lucide-react';

import { useAppDispatch } from '../../../hooks.ts';
import { useStore } from '../../../pages/workspace/hooks.ts';
import {
  deleteMarketMatchGroup,
  loadLampSimilarityDetails,
  updateLampMarketMatchGroup,
  updateLampProductVariant,
  updateLampRotationRadians,
} from '../../../store/reducers/workspace.reducer.ts';
import { Lamp } from '../../../types/lamp.ts';
import { fetchCatalogueProduct } from '../../../utils/catalogueProductCache';
import { buildCatalogueUrl } from '../../../utils/catalogueUrl';
import { canEditMarketMatchGroup, canModifyWorkspace } from '../../../utils/jobStatus';
import { getAppliedMarketMatchForLuminaire, isMarketMatchedVersion } from '../../../utils/lampMarketMatch';
import { ProductVariantPicker } from './ProductVariantPicker';
import type { ProductVariantSelection } from './ProductVariantPicker';

export type LampPopupData = {
  lampId: string;
  screenX: number;
  screenY: number;
};

type Props = {
  data: LampPopupData;
  onClose: () => void;
};

export function LampQuickEditPopup({ data, onClose }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    lamps,
    updateLampName,
    updateLampPosition,
    activeVersion,
    appliedLampSimilaritySelection,
    lampSimilarityDetails,
    isLoadingLampSimilarityDetails,
  } = useStore();
  const popupRef = useRef<HTMLDivElement>(null);
  const groupBtnRef = useRef<HTMLButtonElement>(null);

  const canEdit = canModifyWorkspace(activeVersion);
  const canEditGroup = canEditMarketMatchGroup(activeVersion);

  const lamp = lamps.find((l: Lamp) => l.id === data.lampId);

  const existingMarketMatchGroups = useMemo(() => {
    const names = lamps
      .map((l: Lamp) => l.marketMatchGroup)
      .filter((group): group is string => !!group && group.trim().length > 0);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [lamps]);

  const marketMatchGroupOptions = useMemo(() => {
    const names = new Set(existingMarketMatchGroups);
    if (lamp?.marketMatchGroup?.trim()) {
      names.add(lamp.marketMatchGroup.trim());
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [existingMarketMatchGroups, lamp?.marketMatchGroup]);

  const hasMarketMatchCandidates = (activeVersion?.lampSimilarityResult?.candidates?.length ?? 0) > 0;

  // Trigger detail loading for the popup's own applied-match section when details are not
  // yet available. The saga has an early-return guard if details are already loaded, so
  // dispatching here is a no-op whenever the sidebar has already fetched them.
  useEffect(() => {
    if (hasMarketMatchCandidates && lampSimilarityDetails.length === 0 && !isLoadingLampSimilarityDetails) {
      dispatch(loadLampSimilarityDetails());
    }
  }, [dispatch, hasMarketMatchCandidates, lampSimilarityDetails.length, isLoadingLampSimilarityDetails]);

  const appliedMarketMatch = useMemo(() => {
    if (!lamp) return null;
    return getAppliedMarketMatchForLuminaire(
      lamp.uuid,
      activeVersion,
      appliedLampSimilaritySelection,
      lampSimilarityDetails,
    );
  }, [lamp, activeVersion, appliedLampSimilaritySelection, lampSimilarityDetails]);

  const showMarketMatchSection =
    !!appliedLampSimilaritySelection &&
    hasMarketMatchCandidates &&
    (isLoadingLampSimilarityDetails || appliedMarketMatch != null);

  // Persisted catalogue assignment (market-matched versions): resolved from the
  // lamp's productId/variantId rather than the ephemeral applied selection.
  const isMarketMatched = isMarketMatchedVersion(activeVersion);
  const lampProductId = lamp?.productId;
  const lampVariantId = lamp?.variantId;
  const [assignedInfo, setAssignedInfo] = useState<{
    productName: string;
    variantName: string;
  } | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (lampProductId == null || lampVariantId == null) {
      setAssignedInfo(null);
      return;
    }
    let cancelled = false;
    void fetchCatalogueProduct(lampProductId).then((info) => {
      if (cancelled) return;
      const variant = info?.variants?.find((v) => Number(v.id) === lampVariantId);
      setAssignedInfo({
        productName: info?.name ?? `Product #${lampProductId}`,
        variantName: variant?.name ?? `#${lampVariantId}`,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [lampProductId, lampVariantId]);

  const handlePickerSelect = (selection: ProductVariantSelection) => {
    if (!lamp) return;
    dispatch(
      updateLampProductVariant({
        id: lamp.id,
        productId: selection.productId,
        variantId: selection.variantId,
        seriesId: selection.seriesId,
        manufacturerId: selection.manufacturerId,
      }),
    );
    setAssignedInfo({ productName: selection.productName, variantName: selection.variantName });
    setShowPicker(false);
  };

  const commitRotation = (raw: string) => {
    if (!lamp) return;
    const deg = parseFloat(raw);
    if (isNaN(deg)) {
      const reset = lamp.rotationRadians != null ? String(radToDeg(lamp.rotationRadians)) : '0';
      setRotationDeg(reset);
      return;
    }
    const normalised = parseFloat((((deg % 360) + 360) % 360).toFixed(1));
    setRotationDeg(String(normalised));
    dispatch(updateLampRotationRadians({ id: lamp.id, rotationRadians: degToRad(normalised) }));
  };

  const handleRotationBlur = () => commitRotation(rotationDeg);

  const [name, setName] = useState(lamp?.name ?? '');
  const [height, setHeight] = useState(String(lamp?.distanceToGround ?? ''));
  const [posX, setPosX] = useState(lamp?.position[0].toFixed(2) ?? '0');
  const [posY, setPosY] = useState(lamp?.position[1].toFixed(2) ?? '0');
  const [posZ, setPosZ] = useState(lamp?.position[2].toFixed(2) ?? '0');

  // Rotation in degrees for display; stored as radians in the lamp model.
  const radToDeg = (r: number) => parseFloat(((r * 180) / Math.PI).toFixed(1));
  const degToRad = (d: number) => (d * Math.PI) / 180;
  const [rotationDeg, setRotationDeg] = useState(
    lamp?.rotationRadians != null ? String(radToDeg(lamp.rotationRadians)) : '0',
  );
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [groupDropUp, setGroupDropUp] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState<string | null>(null);

  // Sync local state when lamp changes externally (e.g. dragged in 3D)
  useEffect(() => {
    if (!lamp) return;
    setPosX(lamp.position[0].toFixed(2));
    setPosY(lamp.position[1].toFixed(2));
    setPosZ(lamp.position[2].toFixed(2));
    setHeight(parseFloat(lamp.distanceToGround.toFixed(2)).toString());
  }, [lamp?.position[0], lamp?.position[1], lamp?.position[2], lamp?.distanceToGround]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync rotation when lamp.rotationRadians changes externally (e.g. product swap resets it)
  useEffect(() => {
    if (!lamp) return;
    setRotationDeg(lamp.rotationRadians != null ? String(radToDeg(lamp.rotationRadians)) : '0');
  }, [lamp?.rotationRadians]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pendingDeleteGroup) return;
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, pendingDeleteGroup]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (pendingDeleteGroup) {
          setPendingDeleteGroup(null);
          return;
        }
        if (groupDropdownOpen) {
          setGroupDropdownOpen(false);
          setNewGroupName('');
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [groupDropdownOpen, onClose, pendingDeleteGroup]);

  if (!lamp) return null;

  const handleNameBlur = () => {
    if (name.trim() && name !== lamp.name) {
      updateLampName(lamp.id, name.trim());
    }
  };

  const handleHeightBlur = () => {
    const val = parseFloat(height);
    if (!isNaN(val) && val >= 0) {
      // Compute ground level from current lamp position and raycast distance
      const currentHeight = lamp.distanceToGround ?? 0;
      if (Math.abs(val - currentHeight) > 0.001) {
        const groundZ = lamp.position[2] - currentHeight;
        const newZ = groundZ + val;
        const newPos: [number, number, number] = [lamp.position[0], lamp.position[1], newZ];
        updateLampPosition(lamp.id, newPos);
      }
    } else {
      setHeight(parseFloat(lamp.distanceToGround.toFixed(2)).toString());
    }
  };

  const handlePositionBlur = (axis: 0 | 1 | 2) => {
    const setters = [setPosX, setPosY, setPosZ];
    const values = [posX, posY, posZ];
    const val = parseFloat(values[axis]);
    if (!isNaN(val) && val !== lamp.position[axis]) {
      const newPos: [number, number, number] = [...lamp.position];
      newPos[axis] = val;
      updateLampPosition(lamp.id, newPos);
    } else {
      setters[axis](lamp.position[axis].toFixed(2));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, onBlur: () => void) => {
    if (e.key === 'Enter') {
      onBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleGroupSelect = (group: string | undefined) => {
    if (!lamp) return;
    if (group === lamp.marketMatchGroup || (!group && !lamp.marketMatchGroup)) {
      setGroupDropdownOpen(false);
      setNewGroupName('');
      return;
    }
    dispatch(updateLampMarketMatchGroup({ id: lamp.id, marketMatchGroup: group }));
    setGroupDropdownOpen(false);
    setNewGroupName('');
  };

  const commitNewMarketMatchGroup = () => {
    if (!lamp) return;
    const trimmed = newGroupName.trim();
    if (!trimmed) return;
    handleGroupSelect(trimmed);
  };

  const handleDeleteGroupRequest = (group: string) => {
    if (!lamp || !canEditGroup) return;
    setPendingDeleteGroup(group);
    setGroupDropdownOpen(false);
    setNewGroupName('');
  };

  const handleDeleteGroupConfirm = () => {
    if (!pendingDeleteGroup) return;
    dispatch(deleteMarketMatchGroup({ groupName: pendingDeleteGroup }));
    setPendingDeleteGroup(null);
  };

  const pendingDeleteGroupCount = pendingDeleteGroup
    ? lamps.filter((l: Lamp) => l.marketMatchGroup === pendingDeleteGroup).length
    : 0;

  const showMarketMatchGroupSection = !isMarketMatchedVersion(activeVersion);
  const currentMarketMatchGroup =
    lamp.marketMatchGroup && lamp.marketMatchGroup.trim().length > 0 ? lamp.marketMatchGroup : null;

  // Position clamped to viewport
  const popupWidth = 260;
  const popupHeight = (showMarketMatchSection ? 210 : 140) + (showMarketMatchGroupSection ? 44 : 0);
  const x = Math.min(data.screenX, window.innerWidth - popupWidth - 16);
  const y = Math.min(data.screenY, window.innerHeight - popupHeight - 16);

  const inputClass = `w-[48px] text-[11px] text-white text-right bg-black/40 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-lime-400 ${
    !canEdit ? 'opacity-60 cursor-not-allowed' : ''
  }`;

  const labelClass = 'text-[11px] text-gray-300 uppercase tracking-wider';
  const controlColWidth = 'w-[72px]';

  return (
    <>
      <div
        ref={popupRef}
        className="fixed z-50 shadow-xl"
        style={{
          left: x,
          top: y,
          minWidth: popupWidth,
          borderRadius: 16,
          background: '#313131',
          border: '1px solid #444',
          padding: '12px 14px',
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Header: lamp icon + name + close */}
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#ff9136' }} />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => handleKeyDown(e, handleNameBlur)}
            disabled={!canEdit}
            className={`text-xs text-white bg-transparent border-b border-transparent focus:border-lime-400 focus:outline-none truncate flex-1 ${
              !canEdit ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          />
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors p-0.5 -mr-1 -mt-1 shrink-0"
          >
            <X size={14} />
          </button>
        </div>

        {/* Height row */}
        <div className="flex items-center mb-2">
          <span className={`${labelClass} flex-1 min-w-0`}>{t('workspace.lampHeight', 'Height')}</span>
          <div className={`${controlColWidth} shrink-0 flex items-center justify-end gap-1`}>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              onBlur={handleHeightBlur}
              onKeyDown={(e) => handleKeyDown(e, handleHeightBlur)}
              disabled={!canEdit}
              className={inputClass}
              step="0.1"
              min="0"
            />
            <span className="text-[10px] text-gray-400 leading-none">m</span>
          </div>
        </div>

        {showMarketMatchGroupSection && (
          <div className="relative mb-2">
            <div className="flex items-center">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <span className={`${labelClass} whitespace-nowrap`}>
                  {t('workspace.marketMatchGroupShort', 'Group')}
                </span>
                <Tooltip
                  title={t(
                    'workspace.marketMatchGroupTooltip',
                    'Used only for market matching: luminaires in the same group receive the same product variant. Does not affect optimization or placement.',
                  )}
                  placement="top"
                  arrow
                >
                  <span className="inline-flex shrink-0">
                    <Info size={12} className="text-gray-500 cursor-help" />
                  </span>
                </Tooltip>
              </div>
              <button
                ref={groupBtnRef}
                type="button"
                onClick={() => {
                  if (!canEditGroup) return;
                  if (!groupDropdownOpen && groupBtnRef.current) {
                    const rect = groupBtnRef.current.getBoundingClientRect();
                    const dropdownHeight = 150;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    setGroupDropUp(spaceBelow < dropdownHeight + 8);
                  }
                  setGroupDropdownOpen((open) => {
                    if (open) setNewGroupName('');
                    return !open;
                  });
                }}
                disabled={!canEditGroup}
                className={`${controlColWidth} shrink-0 flex items-center justify-between gap-1 h-6 text-xs rounded px-1.5 ${
                  canEditGroup ? 'cursor-pointer hover:bg-stone-600' : 'cursor-not-allowed opacity-50'
                }`}
                style={{ backgroundColor: '#232323' }}
              >
                <span
                  className={`truncate min-w-0 text-left ${
                    currentMarketMatchGroup ? 'text-lime-400 font-medium' : 'text-gray-400'
                  }`}
                >
                  {currentMarketMatchGroup ?? t('workspace.marketMatchGroupNone', 'None')}
                </span>
                <ChevronDown
                  size={10}
                  className={`text-gray-400 transition-transform shrink-0 ${groupDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            {groupDropdownOpen && (
              <div
                className={`absolute right-0 w-[140px] rounded-lg z-10 ${groupDropUp ? 'mb-1' : 'mt-1'}`}
                style={{
                  backgroundColor: '#232323',
                  border: '1px solid #444',
                  ...(groupDropUp ? { bottom: '100%' } : {}),
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      commitNewMarketMatchGroup();
                    }
                  }}
                  placeholder={t('workspace.marketMatchGroupNewPlaceholder', 'Group name')}
                  autoFocus
                  className="w-full text-xs text-white bg-black/30 px-2 py-1 focus:outline-none border-b border-gray-600 rounded-t-lg"
                />
                <div className="overflow-y-auto" style={{ maxHeight: 120 }}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleGroupSelect(undefined)}
                    className={`w-full text-left text-xs px-2 py-1 hover:bg-stone-600 transition-colors ${
                      !currentMarketMatchGroup ? 'text-lime-400 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {t('workspace.marketMatchGroupNone', 'None')}
                  </button>
                  {marketMatchGroupOptions.map((group) => (
                    <div key={group} className="flex items-center hover:bg-stone-600/60 group/row">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleGroupSelect(group)}
                        className={`flex-1 min-w-0 text-left text-xs px-2 py-1 transition-colors truncate ${
                          currentMarketMatchGroup === group ? 'text-lime-400 font-medium' : 'text-gray-300'
                        }`}
                      >
                        {group}
                      </button>
                      {canEditGroup && (
                        <Tooltip title={t('workspace.marketMatchGroupDelete', 'Delete group')} placement="top" arrow>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleDeleteGroupRequest(group)}
                            aria-label={t('workspace.marketMatchGroupDelete', 'Delete group')}
                            className="shrink-0 p-1 mr-0.5 text-gray-500 hover:text-red-400 transition-colors opacity-70 group-hover/row:opacity-100"
                          >
                            <Trash2 size={11} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Persisted catalogue assignment (market-matched versions) */}
        {(lampVariantId != null || isMarketMatched) && (
          <div className="mt-2 pt-2 border-t border-gray-600">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">
              {t('workspace.assignedProduct')}
            </p>
            {lampVariantId != null && lampProductId != null ? (
              <div className="space-y-1">
                <p className="text-[11px] text-white font-medium truncate">
                  {assignedInfo?.productName ?? `Product #${lampProductId}`}
                </p>
                <p className="text-[11px] text-lime-400">
                  <span className="text-gray-400">{t('workspace.marketMatchVariant')}: </span>
                  {assignedInfo?.variantName ?? `#${lampVariantId}`}
                </p>
                <a
                  href={buildCatalogueUrl({
                    productId: lampProductId,
                    seriesId: lamp?.seriesId,
                    manufacturerId: lamp?.manufacturerId,
                    variantId: lampVariantId,
                  })}
                  className="text-[10px] text-lime-400 hover:underline inline-block"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('workspace.viewInCatalogue')}
                </a>
                {/* Rotation control */}
                <div className="pt-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                      {t('workspace.rotation', 'Rotation')}
                    </span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={rotationDeg}
                        onChange={(e) => setRotationDeg(e.target.value)}
                        onBlur={handleRotationBlur}
                        onKeyDown={(e) => handleKeyDown(e, handleRotationBlur)}
                        disabled={!canEdit}
                        className={inputClass}
                        step="0.1"
                        min="0"
                        max="360"
                      />
                      <span className="text-[10px] text-gray-400">°</span>
                    </div>
                  </div>
                  {canEdit && (
                    <p className="text-[10px] text-gray-500 leading-tight">
                      {t('workspace.rotateInSceneHint', 'Drag the ring in the 3D scene to rotate')}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-gray-400">{t('workspace.noProductAssigned')}</p>
            )}
            {canEdit && (
              <button
                onClick={() => setShowPicker(true)}
                className="mt-1.5 text-[11px] font-semibold px-2 py-1 rounded bg-stone-700 text-white hover:bg-lime-500 hover:text-black transition-colors"
              >
                {lampVariantId != null ? t('workspace.changeProduct') : t('workspace.assignProduct')}
              </button>
            )}
          </div>
        )}

        {showMarketMatchSection && (
          <div className="mt-2 pt-2 border-t border-gray-600">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">
              {t('workspace.appliedMarketMatch')}
            </p>
            {isLoadingLampSimilarityDetails && !appliedMarketMatch ? (
              <p className="text-[11px] text-gray-400">{t('workspace.loadingLampDetails')}</p>
            ) : (
              appliedMarketMatch && (
                <div className="space-y-1">
                  {appliedMarketMatch.manufacturerName && (
                    <p className="text-[11px] text-gray-400 truncate">{appliedMarketMatch.manufacturerName}</p>
                  )}
                  <p className="text-[11px] text-white font-medium truncate">{appliedMarketMatch.productName}</p>
                  <p className="text-[11px] text-lime-400">
                    <span className="text-gray-400">{t('workspace.marketMatchVariant')}: </span>
                    {appliedMarketMatch.variantName}
                  </p>
                  <a
                    href={buildCatalogueUrl({
                      productId: appliedMarketMatch.productId,
                      seriesId: appliedMarketMatch.seriesId,
                      manufacturerId: appliedMarketMatch.manufacturerId,
                      variantId: appliedMarketMatch.variantId,
                    })}
                    className="text-[10px] text-lime-400 hover:underline inline-block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('workspace.viewInCatalogue')}
                  </a>
                </div>
              )
            )}
          </div>
        )}

        {/* Position — X Y Z in one row */}
        <div className="mt-2 pt-2 border-t border-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-red-400 font-medium">X</span>
            <input
              type="number"
              value={posX}
              onChange={(e) => setPosX(e.target.value)}
              onBlur={() => handlePositionBlur(0)}
              onKeyDown={(e) => handleKeyDown(e, () => handlePositionBlur(0))}
              disabled={!canEdit}
              className={inputClass}
              step="0.1"
            />
            <span className="text-[10px] text-green-400 font-medium">Y</span>
            <input
              type="number"
              value={posY}
              onChange={(e) => setPosY(e.target.value)}
              onBlur={() => handlePositionBlur(1)}
              onKeyDown={(e) => handleKeyDown(e, () => handlePositionBlur(1))}
              disabled={!canEdit}
              className={inputClass}
              step="0.1"
            />
            <span className="text-[10px] text-blue-400 font-medium">Z</span>
            <input
              type="number"
              value={posZ}
              onChange={(e) => setPosZ(e.target.value)}
              onBlur={() => handlePositionBlur(2)}
              onKeyDown={(e) => handleKeyDown(e, () => handlePositionBlur(2))}
              disabled={!canEdit}
              className={inputClass}
              step="0.1"
            />
          </div>
        </div>

        {showPicker && (
          <ProductVariantPicker
            initialSelection={
              lamp?.productId != null && lamp.variantId != null && lamp.seriesId != null && lamp.manufacturerId != null
                ? {
                    manufacturerId: lamp.manufacturerId,
                    seriesId: lamp.seriesId,
                    productId: lamp.productId,
                    variantId: lamp.variantId,
                  }
                : null
            }
            onSelect={handlePickerSelect}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>

      {pendingDeleteGroup && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setPendingDeleteGroup(null)}
        >
          <div
            className="bg-[#2a2a2a] border border-[#3a3a3a] rounded-lg p-5 max-w-sm w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white mb-2">
              {t('workspace.marketMatchGroupConfirmDeleteTitle', 'Delete group?')}
            </h3>
            <p className="text-xs text-gray-300 mb-5 leading-relaxed">
              {t('workspace.marketMatchGroupConfirmDeleteMessage', {
                name: pendingDeleteGroup,
                count: pendingDeleteGroupCount,
                defaultValue:
                  'Are you sure you want to delete the group "{{name}}"? This will unassign {{count}} luminaire(s).',
              })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDeleteGroup(null)}
                className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteGroupConfirm}
                className="px-3 py-1.5 text-xs text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
              >
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
